import { useAuth } from "../contexts/AuthContext";

export interface UserPermissions {
  canEditReservations: boolean;
  canDeleteReservations: boolean;
  canCreateReservations: boolean;
  isAdmin: boolean; // Admin normal (admin + super_admin)
  isSuperAdmin: boolean; // 🔥 MODO DIOS: Solo super_admin tiene acceso completo
  isCleaningStaff: boolean;
  isRestrictedUser: boolean;
  restrictionMessage?: string;
}

// 🎯 DEFINICIÓN DE ROLES Y PERMISOS
const ROLES = {
  SUPER_ADMIN: "super_admin",
  ADMIN: "admin",
  MANAGER: "manager",
  LIMPIEZA: "limpieza",
} as const;

const ROLE_PERMISSIONS = {
  [ROLES.SUPER_ADMIN]: {
    canEditReservations: true,
    canDeleteReservations: true,
    canCreateReservations: true,
    isAdmin: true, // Admin normal
    isSuperAdmin: true, // 🔥 MODO DIOS activado
    isCleaningStaff: false,
    isRestrictedUser: false,
  },
  [ROLES.ADMIN]: {
    canEditReservations: true,
    canDeleteReservations: true,
    canCreateReservations: true,
    isAdmin: true, // Admin normal
    isSuperAdmin: false, // NO tiene acceso al modo dios
    isCleaningStaff: false,
    isRestrictedUser: false,
  },
  [ROLES.MANAGER]: {
    canEditReservations: false,
    canDeleteReservations: false,
    canCreateReservations: true, // Puede crear pero no editar
    isAdmin: false,
    isSuperAdmin: false, // NO tiene acceso al modo dios
    isCleaningStaff: false,
    isRestrictedUser: true,
    restrictionMessage:
      "Para editar reservas comunicarse con Mariela Mendes Nunes",
  },
  [ROLES.LIMPIEZA]: {
    canEditReservations: false,
    canDeleteReservations: false,
    canCreateReservations: false,
    isAdmin: false,
    isSuperAdmin: false,
    isCleaningStaff: true,
    isRestrictedUser: true,
    restrictionMessage: "Acceso limitado a limpieza diaria",
  },
};

// 🚨 EXCEPCIONES ESPECÍFICAS POR EMAIL (solo si es necesario)
const EMAIL_EXCEPTIONS = {
  "gabriel@679inn.com.ar": {
    restrictionMessage:
      "Para editar reservas comunicarse con Mariela Mendes Nunes",
  },
};

export const usePermissions = (): UserPermissions => {
  const { user } = useAuth();

  if (!user) {
    return {
      canEditReservations: false,
      canDeleteReservations: false,
      canCreateReservations: false,
      isAdmin: false,
      isSuperAdmin: false,
      isCleaningStaff: false,
      isRestrictedUser: false,
    };
  }

  // Obtener permisos base según el rol
  const basePermissions =
    ROLE_PERMISSIONS[user.rol as keyof typeof ROLE_PERMISSIONS];

  if (!basePermissions) {
    // Rol no reconocido - permisos mínimos
    return {
      canEditReservations: false,
      canDeleteReservations: false,
      canCreateReservations: false,
      isAdmin: false,
      isSuperAdmin: false,
      isCleaningStaff: false,
      isRestrictedUser: true,
      restrictionMessage: "Rol no reconocido. Contacta al administrador.",
    };
  }

  // Aplicar excepciones específicas por email si existen
  const emailException =
    EMAIL_EXCEPTIONS[user.email as keyof typeof EMAIL_EXCEPTIONS];

  return {
    ...basePermissions,
    ...emailException,
  };
};

export default usePermissions;
